<template>
  <ElDialog
    v-model="visible"
    :title="title"
    :width="width"
    :fullscreen="fullscreen"
    :top="top"
    :modal="modal"
    :modal-class="modalClass"
    :append-to-body="appendToBody"
    :lock-scroll="lockScroll"
    :custom-class="customClass"
    :open-delay="openDelay"
    :close-delay="closeDelay"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :show-close="showClose"
    :before-close="handleBeforeClose"
    :center="center"
    :align-center="alignCenter"
    :destroy-on-close="destroyOnClose"
    @open="handleOpen"
    @opened="handleOpened"
    @close="handleClose"
    @closed="handleClosed"
  >
    <!-- 头部插槽 -->
    <template v-if="$slots.header" #header="{ close, titleId, titleClass }">
      <slot
        name="header"
        :close="close"
        :title-id="titleId"
        :title-class="titleClass"
      ></slot>
    </template>

    <!-- 默认内容插槽 -->
    <slot>
      <div v-if="content" class="modal-content">
        {{ content }}
      </div>
    </slot>

    <!-- 底部插槽 -->
    <template v-if="$slots.footer || showDefaultFooter" #footer>
      <slot name="footer">
        <div v-if="showDefaultFooter" class="modal-footer">
          <ElButton @click="handleCancel">
            {{ cancelText }}
          </ElButton>
          <ElButton
            type="primary"
            :loading="confirmLoading"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </ElButton>
        </div>
      </slot>
    </template>
  </ElDialog>
</template>

<script setup lang="ts">
/**
 * Modal 模态框组件
 * 基于 Element Plus Dialog 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ref, computed, watch } from 'vue'
import type { DialogBeforeCloseFn } from 'element-plus'

// 接口定义
interface Props {
  /** 是否显示 */
  modelValue?: boolean
  /** 标题 */
  title?: string
  /** 宽度 */
  width?: string | number
  /** 是否全屏 */
  fullscreen?: boolean
  /** 距离顶部的距离 */
  top?: string
  /** 是否显示遮罩层 */
  modal?: boolean
  /** 遮罩层类名 */
  modalClass?: string
  /** 是否插入到 body 元素上 */
  appendToBody?: boolean
  /** 是否锁定滚动 */
  lockScroll?: boolean
  /** 自定义类名 */
  customClass?: string
  /** 打开延时 */
  openDelay?: number
  /** 关闭延时 */
  closeDelay?: number
  /** 是否可以通过点击遮罩层关闭 */
  closeOnClickModal?: boolean
  /** 是否可以通过按下 ESC 关闭 */
  closeOnPressEscape?: boolean
  /** 是否显示关闭按钮 */
  showClose?: boolean
  /** 是否居中布局 */
  center?: boolean
  /** 是否水平垂直对齐于屏幕中心 */
  alignCenter?: boolean
  /** 关闭时销毁子元素 */
  destroyOnClose?: boolean
  /** 内容文本 */
  content?: string
  /** 是否显示默认底部 */
  showDefaultFooter?: boolean
  /** 取消按钮文本 */
  cancelText?: string
  /** 确认按钮文本 */
  confirmText?: string
  /** 确认按钮加载状态 */
  confirmLoading?: boolean
}

interface Emits {
  /** 更新显示状态 */
  'update:modelValue': [value: boolean]
  /** 打开事件 */
  open: []
  /** 打开后事件 */
  opened: []
  /** 关闭事件 */
  close: []
  /** 关闭后事件 */
  closed: []
  /** 取消事件 */
  cancel: []
  /** 确认事件 */
  confirm: []
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: '',
  width: '50%',
  fullscreen: false,
  top: '15vh',
  modal: true,
  modalClass: '',
  appendToBody: false,
  lockScroll: true,
  customClass: '',
  openDelay: 0,
  closeDelay: 0,
  closeOnClickModal: true,
  closeOnPressEscape: true,
  showClose: true,
  center: false,
  alignCenter: false,
  destroyOnClose: false,
  content: '',
  showDefaultFooter: false,
  cancelText: '取消',
  confirmText: '确定',
  confirmLoading: false
})

// 定义 emits
const emit = defineEmits<Emits>()

// 内部显示状态
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)
  }
})

/**
 * 关闭前处理函数
 */
const handleBeforeClose: DialogBeforeCloseFn = (done) => {
  // 可以在这里添加关闭前的确认逻辑
  done()
}

/**
 * 打开事件处理
 */
const handleOpen = (): void => {
  emit('open')
}

/**
 * 打开后事件处理
 */
const handleOpened = (): void => {
  emit('opened')
}

/**
 * 关闭事件处理
 */
const handleClose = (): void => {
  emit('close')
}

/**
 * 关闭后事件处理
 */
const handleClosed = (): void => {
  emit('closed')
}

/**
 * 取消按钮点击处理
 */
const handleCancel = (): void => {
  visible.value = false
  emit('cancel')
}

/**
 * 确认按钮点击处理
 */
const handleConfirm = (): void => {
  emit('confirm')
}

/**
 * 手动打开模态框
 */
const open = (): void => {
  visible.value = true
}

/**
 * 手动关闭模态框
 */
const close = (): void => {
  visible.value = false
}

// 暴露方法
defineExpose({
  open,
  close
})
</script>

<style scoped>
.modal-content {
  padding: 20px 0;
  line-height: 1.6;
  color: var(--el-text-color-primary);
  font-size: 14px;
  min-height: 60px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
}

/* 自定义样式变量 */
:deep(.el-dialog) {
  --el-dialog-border-radius: 8px;
  border-radius: var(--el-dialog-border-radius);
}

:deep(.el-dialog__header) {
  padding: 20px 20px 10px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

:deep(.el-dialog__body) {
  padding: 20px;
  color: var(--el-text-color-primary);
}

:deep(.el-dialog__footer) {
  padding: 10px 20px 20px;
  border-top: 1px solid var(--el-border-color-lighter);
}

/* 响应式设计 */
@media (max-width: 768px) {
  :deep(.el-dialog) {
    width: 95% !important;
    margin-top: 5vh !important;
  }

  :deep(.el-dialog__body) {
    padding: 15px;
  }

  .modal-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .modal-footer .el-button {
    width: 100%;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  :deep(.el-dialog) {
    background-color: var(--el-bg-color-page);
  }
}
</style>
